import { useEffect } from 'react';

import { useAppDispatch, useAppSelector } from 'features/app/hooks';
import {
  VoiceMeetingEventBus,
  VoiceMeetingService,
} from 'features/voice-meeting/services';
import {
  setCallDirection,
  callDirectionSelector,
} from 'features/call/call-hearing/store';

/**
 * Hook that tracks whether a Chime hearing call is inbound or outbound
 * Agent Places Hearing Call -> Outbound
 * Agent Receives Hearing Call -> Inbound
 * These are not related exclusively to H2D or D2H calls as H2D can be inbound or outbound
 * D2H can be outbound only
 * @returns Object containing the current call direction
 */
export const useVoiceCallDirection = () => {
  const dispatch = useAppDispatch();
  // Get the current direction from Redux state instead of local state
  const currentDirection = useAppSelector(callDirectionSelector);
  const voiceMeetingService = VoiceMeetingService.getInstance();

  useEffect(() => {
    // Set initial value from existing session if available
    const initialDirection = voiceMeetingService.getCallDirection();

    // Only dispatch if there's a direction and it's different from current state
    if (initialDirection && initialDirection !== currentDirection) {
      dispatch(setCallDirection(initialDirection));
    }

    // Also listen for reset events to ensure direction is reset to outbound on new sessions
    const resetSubscription = VoiceMeetingEventBus.resetVoiceSession$.subscribe(
      () => {
        // Only dispatch outbound if current direction is not already outbound
        if (currentDirection !== 'outbound') {
          dispatch(setCallDirection('outbound'));
        }
      }
    );

    return () => {
      resetSubscription.unsubscribe();
    };
  }, [dispatch, voiceMeetingService, currentDirection]);

  return {
    callDirection: currentDirection,
    isInbound: currentDirection === 'inbound',
    isOutbound: currentDirection === 'outbound',
  };
};
